import React, { useState, useCallback } from 'react';
import mammoth from 'mammoth';
import { tournamentService } from '../services/tournamentService';
import QuestionEditor from './QuestionEditor';

const QuizParser = ({ onSave }) => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ✅ V5: Use HTML conversion to preserve numbering
  class QuizGrammarParser {
    constructor() {
      this.currentQuestionId = 1;
    }

    async parseDocument(file) {
      const arrayBuffer = await file.arrayBuffer();
      
      // ✅ Use HTML conversion instead of raw text
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const html = result.value;
      
      // Parse HTML to extract numbered items
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get all list items (these are the numbered questions)
      const listItems = Array.from(doc.querySelectorAll('ol li'));
      
      console.log(`Total numbered items found: ${listItems.length}`);
      
      // Extract metadata from non-list content
      const metadata = this.extractMetadata(doc);
      
      // Parse questions from list items
      const questions = this.parseQuestionsFromListItems(listItems);
      
      console.log(`✓ Found ${questions.length} questions`);
      
      return {
        id: Date.now(),
        title: metadata.title || 'Unknown',
        difficulty: metadata.difficulty || 'Mixed',
        questions: questions.length,
        author: metadata.authors || [],
        date: metadata.date || '',
        questions_data: questions,
        created_at: new Date().toISOString()
      };
    }

    parseQuestionsFromListItems(listItems) {
      const questions = [];
      
      for (let i = 0; i < listItems.length; i++) {
        const li = listItems[i];
        const text = li.textContent.trim();
        
        if (!text || text.length < 10) continue;
        
        // Parse this list item as a question
        const question = this.parseQuestion(text);
        if (question) {
          questions.push(question);
        }
      }
      
      return questions;
    }

    parseQuestion(text) {
      const question = {
        id: this.currentQuestionId++,
        text: '',
        answer: '',
        comment: '',
        source: '',
        url: '',
        image: null,
        author: 'Unknown'
      };

      // Find keyword positions
      const answerPos = text.indexOf('პასუხი:');
      const commentPos = text.indexOf('კომენტარი:');
      const sourcePos = text.indexOf('წყარო:');
      const authorPos = text.indexOf('ავტორი:');
      const acceptablePos = text.indexOf('ჩათვლა:');

      // ✅ FIX: DON'T skip questions without answers
      // Store question text and mark as incomplete
      if (answerPos === -1) {
        question.text = this.cleanQuestionText(text);
        question.answer = '[INCOMPLETE - Answer not found in numbered item]';
        return question;
      }

      // Extract question text
      const questionText = text.substring(0, answerPos).trim();
      question.text = this.cleanQuestionText(questionText);

      // Extract answer
      let answerEnd = text.length;
      const nextAfterAnswer = [commentPos, sourcePos, authorPos, acceptablePos]
        .filter(pos => pos > answerPos);
      if (nextAfterAnswer.length > 0) {
        answerEnd = Math.min(...nextAfterAnswer);
      }
      question.answer = text.substring(answerPos + 7, answerEnd).trim();

      // Extract comment
      if (commentPos > answerPos) {
        let commentEnd = text.length;
        const nextAfterComment = [sourcePos, authorPos]
          .filter(pos => pos > commentPos);
        if (nextAfterComment.length > 0) {
          commentEnd = Math.min(...nextAfterComment);
        }
        question.comment = text.substring(commentPos + 11, commentEnd).trim();
      }

      // Extract acceptable answers
      if (acceptablePos > answerPos) {
        let acceptableEnd = text.length;
        const nextAfterAcceptable = [commentPos, sourcePos, authorPos]
          .filter(pos => pos > acceptablePos);
        if (nextAfterAcceptable.length > 0) {
          acceptableEnd = Math.min(...nextAfterAcceptable);
        }
        const acceptable = text.substring(acceptablePos + 8, acceptableEnd).trim();
        if (acceptable) {
          question.answer += ` [ჩათვლა: ${acceptable}]`;
        }
      }

      // Extract source
      if (sourcePos > answerPos) {
        let sourceEnd = text.length;
        if (authorPos > sourcePos) {
          sourceEnd = authorPos;
        }
        question.source = text.substring(sourcePos + 6, sourceEnd).trim();
        
        const urlMatch = question.source.match(/https?:\/\/[^\s]+/);
        if (urlMatch) {
          question.url = urlMatch[0];
        }
      }

      // Extract author
      if (authorPos > answerPos) {
        question.author = text.substring(authorPos + 7).trim();
      }

      return question;
    }

    cleanQuestionText(text) {
      return text
        .replace(/\[დასარიგებელი მასალა:?\s*\]/g, '')
        .replace(/გირჩევთ,?\s*ჩაიწეროთ\.?/g, '')
        .replace(/ყურადღება,?\s*შეკითხვაში/g, '')
        .trim();
    }

    extractMetadata(doc) {
      const metadata = {
        title: '',
        date: '',
        authors: [],
        difficulty: 'Mixed'
      };

      // Get all paragraphs before first list
      const firstList = doc.querySelector('ol');
      if (!firstList) return metadata;

      const allElements = Array.from(doc.body.children);
      const listIndex = allElements.indexOf(firstList);
      
      for (let i = 0; i < listIndex && i < 5; i++) {
        const elem = allElements[i];
        const text = elem.textContent.trim();
        
        if (i === 0) {
          metadata.title = text;
          
          if (text.includes('სუპერლიგა')) {
            metadata.difficulty = 'Advanced';
          } else if (text.includes('პირველი ლიგა')) {
            metadata.difficulty = 'Intermediate';
          } else if (text.includes('მეორე ლიგა')) {
            metadata.difficulty = 'Beginner';
          }
        }

        // Extract date
        const georgianMonths = /თებერვალი|მარტი|აპრილი|მაისი|ივნისი|ივლისი|აგვისტო|სექტემბერი|ოქტომბერი|ნოემბერი|დეკემბერი/i;
        if (georgianMonths.test(text)) {
          metadata.date = text;
        }

        // Extract authors
        if (text.includes('ავტორ-რედაქტორ')) {
          const namesWithCounts = [...text.matchAll(/([^\(,]+)\s*\((\d+(?:\.\d+)?)\)/g)];
          if (namesWithCounts.length > 0) {
            metadata.authors = namesWithCounts.map(m => `${m[1].trim()} (${m[2]})`);
          }
        }
      }

      return metadata;
    }

    reset() {
      this.currentQuestionId = 1;
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.docx')) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
      setSaveSuccess(false);
    } else {
      setError('გთხოვთ აირჩიოთ .docx ფაილი');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.docx')) {
      setFile(droppedFile);
      setError(null);
      setResult(null);
      setSaveSuccess(false);
    } else {
      setError('გთხოვთ ჩააგდოთ .docx ფაილი');
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleParse = async () => {
    if (!file) return;

    setParsing(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const parser = new QuizGrammarParser();
      const packet = await parser.parseDocument(file);
      setResult(packet);
      console.log('✓ Parsed packet:', packet);
    } catch (err) {
      console.error('Parsing error:', err);
      setError('შეცდომა დამუშავებისას: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await tournamentService.saveTournament(result);
      
      console.log('✓ Tournament saved to Supabase!');
      setSaveSuccess(true);
      
      if (onSave) {
        onSave();
      }
      
    } catch (err) {
      console.error('Save error:', err);
      setError('შეცდომა შენახვისას: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionsUpdate = (updatedQuestions) => {
    setResult({
      ...result,
      questions: updatedQuestions.length,
      questions_data: updatedQuestions
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div
          style={{
            ...styles.uploadZone,
            ...(file ? styles.uploadZoneHasFile : {})
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.getElementById('fileInput').click()}
        >
          <div style={styles.uploadIcon}>
            {file ? '📄' : '📁'}
          </div>
          <p>{file ? file.name : 'ჩააგდეთ .docx ფაილი აქ ან დააჭირეთ არჩევისთვის'}</p>
          <input
            id="fileInput"
            type="file"
            accept=".docx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        {file && !result && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button 
              style={styles.parseButton}
              onClick={handleParse}
              disabled={parsing}
            >
              {parsing ? 'დამუშავება...' : '🚀 დამუშავება'}
            </button>
          </div>
        )}

        {parsing && <div style={styles.spinner} />}

        {error && (
          <div style={styles.errorMessage}>
            ❌ {error}
          </div>
        )}

        {saveSuccess && (
          <div style={styles.successMessage}>
            ✓ წარმატებით შეინახა მონაცემთა ბაზაში!
          </div>
        )}

        {result && (
          <div style={{ marginTop: '30px' }}>
            <h3>📊 შედეგები</h3>
            
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{result.questions}</div>
                <div style={styles.statLabel}>კითხვები</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{result.difficulty}</div>
                <div style={styles.statLabel}>სირთულე</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{result.author.length}</div>
                <div style={styles.statLabel}>ავტორები</div>
              </div>
            </div>

            <div style={styles.fileInfo}>
              <strong>სათაური:</strong> {result.title}<br />
              <strong>თარიღი:</strong> {result.date}<br />
              <strong>ავტორები:</strong> {result.author.join(', ')}
            </div>

            <QuestionEditor 
              questions={result.questions_data}
              onUpdate={handleQuestionsUpdate}
              metadata={{
                id: result.id,
                title: result.title,
                difficulty: result.difficulty,
                date: result.date,
                author: result.author,
                created_at: result.created_at
              }}
            />

            <div style={{ textAlign: 'center', marginTop: '30px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
              <button 
                style={styles.saveButton}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'ინახება...' : '💾 შენახვა მონაცემთა ბაზაში'}
              </button>
              <button 
                style={styles.resetButton}
                onClick={() => {
                  setFile(null);
                  setResult(null);
                  setError(null);
                  setSaveSuccess(false);
                }}
              >
                🔄 ახალი ფაილი
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '30px',
  },
  uploadZone: {
    border: '3px dashed #667eea',
    borderRadius: '12px',
    padding: '60px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: '#f8f9ff',
  },
  uploadZoneHasFile: {
    borderColor: '#4caf50',
    background: '#f0fff4',
  },
  uploadIcon: {
    fontSize: '4em',
    marginBottom: '20px',
  },
  parseButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 30px',
    fontSize: '1em',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  saveButton: {
    background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 30px',
    fontSize: '1em',
    borderRadius: '8px',
    cursor: 'pointer',
    margin: '10px 5px',
    fontWeight: 'bold',
  },
  resetButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 30px',
    fontSize: '1em',
    borderRadius: '8px',
    cursor: 'pointer',
    margin: '10px 5px',
    fontWeight: 'bold',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
    margin: '20px 0',
  },
  statCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2em',
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: '0.9em',
    opacity: 0.9,
  },
  errorMessage: {
    background: '#fee',
    border: '2px solid #fcc',
    color: '#c33',
    padding: '15px',
    borderRadius: '8px',
    margin: '15px 0',
  },
  successMessage: {
    background: '#efe',
    border: '2px solid #cfc',
    color: '#3c3',
    padding: '15px',
    borderRadius: '8px',
    margin: '15px 0',
  },
  jsonPreview: {
    background: '#1e1e1e',
    color: '#d4d4d4',
    padding: '20px',
    borderRadius: '8px',
    overflow: 'auto',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.9em',
    maxHeight: '400px',
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '20px auto',
  },
  fileInfo: {
    margin: '15px 0',
    padding: '15px',
    background: '#f5f5f5',
    borderRadius: '6px',
  }
};


export default QuizParser;